'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type { CSSProperties } from 'react';
import { RotateCw } from 'lucide-react';
import { Button } from '@upzi/web-shell/ui/button';
import { cn } from '@/lib/utils';
import type {
  JobFilterOption,
  JobFilterOrderItem,
  JobFilters,
  JobFilterState,
  JobMultiFilterKey,
  JobSingleFilterKey,
} from '@/services/jobs/model';
import {
  getActiveJobFilterCount,
  getJobFilterDefinition,
  getOrderedJobFilterGroups,
} from '../filter-config';
import { useDesktopJobFilters } from '../hooks/use-desktop-job-filters';
import { LazyMobileJobFilterSheet } from './lazy-mobile-job-filter-sheet';
import { LazyMobileJobFilterControlSheet } from './lazy-mobile-job-filter-control-sheet';
import { LazyMobileLocationSheet } from './lazy-mobile-location-sheet';
import { JobSearchHero } from './job-search-hero';

const SCROLL_DIRECTION_THRESHOLD = 8;
const FILTER_TRANSITION_DURATION_MS = 300;

// CSS-hidden desktop controls would still preload their popover, checkbox and
// radio implementations on mobile. Mount this chunk only at the sm breakpoint.
const JobFilterControl = dynamic(() =>
  import('./job-filter-control').then(module => module.JobFilterControl)
);

type JobFilterListProps = {
  initialDesktopViewport?: boolean;
  filterOptions: Partial<JobFilters>;
  filterOrder: JobFilterOrderItem[];
  filters: JobFilterState;
  isSearching: boolean;
  isSuggestionLoading: boolean;
  locationOptions: JobFilterOption[];
  onClearFilters: () => void;
  onFixedHeightChange: (height: number) => void;
  onPinnedSearchVisibilityChange: (isVisible: boolean) => void;
  onFiltersChange: (filters: JobFilterState) => void;
  onMultiFilterChange: (key: JobMultiFilterKey, value: string[]) => void;
  onQueryChange: (value: string) => void;
  onSuggestionsActivate?: () => void;
  onSearchSubmit: (query: string) => void;
  onSingleFilterChange: (key: JobSingleFilterKey, value: string) => void;
  query: string;
  suggestionKeywords: string[];
};

export const JobFilterList = memo(function JobFilterList({
  initialDesktopViewport = false,
  filterOptions,
  filterOrder,
  filters,
  isSearching,
  isSuggestionLoading,
  locationOptions,
  onClearFilters,
  onFixedHeightChange,
  onPinnedSearchVisibilityChange,
  onFiltersChange,
  onMultiFilterChange,
  onQueryChange,
  onSuggestionsActivate,
  onSearchSubmit,
  onSingleFilterChange,
  query,
  suggestionKeywords,
}: JobFilterListProps) {
  const isDesktopViewport = useDesktopJobFilters(initialDesktopViewport);
  const activeFilterCount = useMemo(
    () => getActiveJobFilterCount(filters, true),
    [filters]
  );
  const { locationGroup, mobileFilterGroups, nonLocationFilters } =
    useMemo(() => {
      const orderedFilters = getOrderedJobFilterGroups(
        filterOptions,
        filterOrder
      );
      const nextNonLocationFilters = orderedFilters.filter(
        group => group.definition.stateKey !== 'location'
      );
      const locationDefinition = getJobFilterDefinition('location');
      const nextLocationGroup =
        locationDefinition && locationOptions.length > 0
          ? { definition: locationDefinition, options: locationOptions }
          : null;

      return {
        locationGroup: nextLocationGroup,
        mobileFilterGroups: nextLocationGroup
          ? [nextLocationGroup, ...nextNonLocationFilters]
          : nextNonLocationFilters,
        nonLocationFilters: nextNonLocationFilters,
      };
    }, [filterOptions, filterOrder, locationOptions]);
  const handleMobileLocationApply = useCallback(
    (value: string[]) => onMultiFilterChange('location', value),
    [onMultiFilterChange]
  );

  const sentinelRef = useRef<HTMLDivElement>(null);
  const filterContentRef = useRef<HTMLDivElement>(null);
  const [isFilterPinned, setIsFilterPinned] = useState(false);
  const [isPinnedSearchVisible, setIsPinnedSearchVisible] = useState(false);
  const [filterContentHeight, setFilterContentHeight] = useState(52);
  const shouldShowPinnedSearch = isFilterPinned && isPinnedSearchVisible;
  const fixedHeight = isFilterPinned
    ? filterContentHeight + (shouldShowPinnedSearch ? 72 : 0)
    : 0;

  useEffect(() => {
    const filterContent = filterContentRef.current;

    if (!filterContent) return;

    const updateFilterContentHeight = () => {
      const nextHeight = Math.ceil(filterContent.offsetHeight);

      setFilterContentHeight(currentHeight =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      );
    };
    const resizeObserver = new ResizeObserver(updateFilterContentHeight);

    updateFilterContentHeight();
    resizeObserver.observe(filterContent);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    onFixedHeightChange(fixedHeight);
  }, [fixedHeight, onFixedHeightChange]);

  useEffect(() => () => onFixedHeightChange(0), [onFixedHeightChange]);

  useEffect(() => {
    onPinnedSearchVisibilityChange(shouldShowPinnedSearch);
  }, [onPinnedSearchVisibilityChange, shouldShowPinnedSearch]);

  useEffect(
    () => () => onPinnedSearchVisibilityChange(false),
    [onPinnedSearchVisibilityChange]
  );

  useEffect(() => {
    let animationFrameId: number | null = null;
    const sentinel = sentinelRef.current;
    const desktopMediaQuery = window.matchMedia('(min-width: 640px)');

    if (!sentinel) return;

    let previousScrollY = window.scrollY;
    let previousScrollDirection = 0;
    let accumulatedScrollDistance = 0;
    let isFilterCurrentlyPinned = false;
    let isPinnedSearchCurrentlyVisible = false;
    let visibilityTransitionEndsAt = 0;

    const updateVisibility = () => {
      animationFrameId = null;

      if (!desktopMediaQuery.matches) {
        if (isFilterCurrentlyPinned) {
          isFilterCurrentlyPinned = false;
          setIsFilterPinned(false);
        }
        if (isPinnedSearchCurrentlyVisible) {
          isPinnedSearchCurrentlyVisible = false;
          setIsPinnedSearchVisible(false);
        }
        previousScrollY = window.scrollY;
        previousScrollDirection = 0;
        accumulatedScrollDistance = 0;
        visibilityTransitionEndsAt = 0;

        return;
      }

      const currentScrollY = window.scrollY;
      const scrollDifference = currentScrollY - previousScrollY;
      const scrollDirection = Math.sign(scrollDifference);
      const hasScrolledPastFilter = sentinel.getBoundingClientRect().top <= 0;

      if (!hasScrolledPastFilter) {
        if (isFilterCurrentlyPinned) {
          isFilterCurrentlyPinned = false;
          setIsFilterPinned(false);
        }
        if (isPinnedSearchCurrentlyVisible) {
          isPinnedSearchCurrentlyVisible = false;
          setIsPinnedSearchVisible(false);
        }
        visibilityTransitionEndsAt = 0;
      } else {
        if (!isFilterCurrentlyPinned) {
          isFilterCurrentlyPinned = true;
          setIsFilterPinned(true);
        }

        if (scrollDirection !== 0) {
          if (scrollDirection !== previousScrollDirection) {
            accumulatedScrollDistance = 0;
          }

          accumulatedScrollDistance += Math.abs(scrollDifference);

          if (
            accumulatedScrollDistance >= SCROLL_DIRECTION_THRESHOLD &&
            performance.now() >= visibilityTransitionEndsAt
          ) {
            const shouldShowSearch = scrollDirection < 0;

            if (shouldShowSearch !== isPinnedSearchCurrentlyVisible) {
              isPinnedSearchCurrentlyVisible = shouldShowSearch;
              visibilityTransitionEndsAt =
                performance.now() + FILTER_TRANSITION_DURATION_MS;
              accumulatedScrollDistance = 0;
              setIsPinnedSearchVisible(shouldShowSearch);
            }
          }
        }
      }

      if (scrollDirection !== 0) previousScrollDirection = scrollDirection;
      previousScrollY = currentScrollY;
    };

    const handleScroll = () => {
      if (animationFrameId !== null) return;

      animationFrameId = window.requestAnimationFrame(updateVisibility);
    };

    const syncScrollListener = () => {
      window.removeEventListener('scroll', handleScroll);
      if (desktopMediaQuery.matches) {
        window.addEventListener('scroll', handleScroll, { passive: true });
      }
      handleScroll();
    };

    updateVisibility();
    syncScrollListener();
    desktopMediaQuery.addEventListener('change', syncScrollListener);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      desktopMediaQuery.removeEventListener('change', syncScrollListener);

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <>
      <div ref={sentinelRef} className="hidden h-px sm:block" aria-hidden />
      <div
        className={cn(
          'bg-surface-pure relative z-30 w-full max-w-full min-w-0 overflow-hidden px-4 py-1.5 sm:sticky sm:top-0 sm:h-[var(--job-filter-layout-height)] sm:w-screen sm:max-w-none sm:overflow-visible sm:bg-transparent sm:p-0 sm:shadow-none'
        )}
        style={
          {
            '--job-filter-layout-height': `${filterContentHeight}px`,
            '--job-filter-visual-height': `${
              fixedHeight || filterContentHeight
            }px`,
          } as CSSProperties
        }
      >
        <div
          aria-hidden
          className={cn(
            'bg-surface shadow-job-filter pointer-events-none absolute inset-x-0 top-0 hidden opacity-0 transition-[height,opacity,box-shadow] duration-500 ease-out motion-reduce:transition-none sm:block sm:h-[var(--job-filter-visual-height)]',
            isFilterPinned &&
              'opacity-100 duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]'
          )}
        />

        <div
          aria-hidden={!shouldShowPinnedSearch}
          inert={shouldShowPinnedSearch ? undefined : true}
          className={cn(
            'pointer-events-none hidden max-h-0 -translate-y-2 overflow-hidden opacity-0 transition-[max-height,opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none sm:absolute sm:inset-x-0 sm:top-0 sm:z-20 sm:block',
            shouldShowPinnedSearch &&
              'pointer-events-auto max-h-[72px] translate-y-0 opacity-100 sm:overflow-visible'
          )}
        >
          {isDesktopViewport ? (
            <JobSearchHero
              key={shouldShowPinnedSearch ? 'pinned-visible' : 'pinned-hidden'}
              isLoading={isSearching}
              isSuggestionsEnabled={shouldShowPinnedSearch}
              isSuggestionLoading={isSuggestionLoading}
              onQueryChange={onQueryChange}
              onSuggestionsActivate={onSuggestionsActivate}
              onSubmit={onSearchSubmit}
              query={query}
              suggestionKeywords={suggestionKeywords}
            />
          ) : null}
        </div>

        <div
          ref={filterContentRef}
          className={cn(
            'relative z-10 flex w-full max-w-full min-w-0 justify-between gap-2 bg-white sm:mx-auto sm:min-h-[52px] sm:max-w-[1440px] sm:items-start sm:px-6 sm:py-2 sm:transition-transform sm:duration-300 sm:ease-[cubic-bezier(0.22,1,0.36,1)] sm:motion-reduce:transition-none lg:px-20',
            shouldShowPinnedSearch && 'sm:translate-y-[72px]'
          )}
        >
          <div className="hide-scrollbar flex w-full max-w-full min-w-0 items-center gap-2 overflow-x-auto overscroll-x-contain sm:hidden md:gap-4">
            <LazyMobileJobFilterSheet
              filters={filters}
              groups={mobileFilterGroups}
              onApply={onFiltersChange}
            />
            <LazyMobileLocationSheet
              options={locationOptions}
              value={filters.location}
              onApply={handleMobileLocationApply}
            />
            {nonLocationFilters.map(group => (
              <LazyMobileJobFilterControlSheet
                key={group.definition.stateKey}
                filters={filters}
                group={group}
                onApply={onFiltersChange}
              />
            ))}
            <Button
              type="button"
              variant="ghost"
              aria-label={`Xóa lọc. ${activeFilterCount} bộ lọc đang được áp dụng`}
              disabled={activeFilterCount === 0}
              onClick={onClearFilters}
              className="text-primary hover:bg-primary-variant disabled:text-alias-content-disabled h-9 shrink-0 gap-1.5 rounded-xl !px-3 text-xs leading-5 font-medium"
            >
              <RotateCw className="size-4" />
              Xóa lọc
            </Button>
          </div>

          <div className="hidden min-w-0 flex-1 flex-wrap items-center gap-3 gap-y-2 overflow-visible sm:flex">
            {isDesktopViewport && locationGroup && (
              <JobFilterControl
                key="location"
                filters={filters}
                group={locationGroup}
                onMultiFilterChange={onMultiFilterChange}
                onSingleFilterChange={onSingleFilterChange}
              />
            )}
            {isDesktopViewport &&
              nonLocationFilters.map(group => (
                <JobFilterControl
                  key={group.definition.stateKey}
                  filters={filters}
                  group={group}
                  onMultiFilterChange={onMultiFilterChange}
                  onSingleFilterChange={onSingleFilterChange}
                />
              ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            aria-label={`Xóa lọc. ${activeFilterCount} bộ lọc đang được áp dụng`}
            disabled={activeFilterCount === 0}
            onClick={onClearFilters}
            className="text-primary hover:bg-primary-variant disabled:text-alias-content-disabled hidden h-9 shrink-0 gap-1.5 rounded-xl !px-3 text-xs leading-5 font-medium sm:flex"
          >
            <RotateCw className="size-4" />
            Xóa lọc
          </Button>
        </div>
      </div>
    </>
  );
});
